<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import JuyangAiAPI from 'dashboard/api/juyangAi';

import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  assistantId: { type: Number, required: true },
  editable: { type: Boolean, default: false },
});

const emit = defineEmits(['changed']);
const { t } = useI18n();
const scenarios = ref([]);
const tools = ref([]);
const isLoading = ref(false);
const isSaving = ref(false);
const editingId = ref(null);
const showForm = ref(false);

const executableTools = computed(() =>
  tools.value.filter(
    tool => tool.approval_status === 'approved' && tool.enabled
  )
);

const form = reactive({
  name: '',
  description: '',
  instructions: '',
  enabled: true,
  tool_ids: [],
});

const errorMessage = error =>
  error?.response?.data?.errors?.join(', ') ||
  error?.response?.data?.error ||
  t('JUYANG_AI.ERROR');

const loadData = async () => {
  isLoading.value = true;
  try {
    const [{ data: scenarioData }, { data: toolData }] = await Promise.all([
      JuyangAiAPI.getNested(props.assistantId, 'scenarios'),
      JuyangAiAPI.getNested(props.assistantId, 'tools'),
    ]);
    scenarios.value = scenarioData;
    tools.value = toolData;
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isLoading.value = false;
  }
};

const resetForm = () => {
  Object.assign(form, {
    name: '',
    description: '',
    instructions: '',
    enabled: true,
    tool_ids: [],
  });
  editingId.value = null;
  showForm.value = false;
};

const beginCreate = () => {
  resetForm();
  showForm.value = true;
};

const beginEdit = scenario => {
  const executableToolIds = new Set(executableTools.value.map(tool => tool.id));
  Object.assign(form, {
    name: scenario.name,
    description: scenario.description,
    instructions: scenario.instructions,
    enabled: scenario.enabled,
    // 过滤历史失效引用，确保重新保存时与后端可执行工具约束一致。
    tool_ids: scenario.tool_ids
      .map(Number)
      .filter(id => executableToolIds.has(id)),
  });
  editingId.value = scenario.id;
  showForm.value = true;
};

const toggleToolSelection = (toolId, selected) => {
  const ids = new Set(form.tool_ids);
  if (selected) ids.add(toolId);
  else ids.delete(toolId);
  form.tool_ids = [...ids];
};

const saveScenario = async () => {
  isSaving.value = true;
  const payload = {
    name: form.name.trim(),
    description: form.description.trim(),
    instructions: form.instructions.trim(),
    enabled: form.enabled,
    tool_ids: form.tool_ids,
  };

  try {
    if (editingId.value) {
      await JuyangAiAPI.updateNested(
        props.assistantId,
        'scenarios',
        editingId.value,
        'scenario',
        payload
      );
      useAlert(t('JUYANG_AI.SCENARIOS.UPDATE_SUCCESS'));
    } else {
      await JuyangAiAPI.createNested(
        props.assistantId,
        'scenarios',
        'scenario',
        payload
      );
      useAlert(t('JUYANG_AI.SCENARIOS.CREATE_SUCCESS'));
    }
    resetForm();
    await loadData();
    emit('changed');
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isSaving.value = false;
  }
};

const toggleScenario = async (scenario, enabled) => {
  try {
    await JuyangAiAPI.updateNested(
      props.assistantId,
      'scenarios',
      scenario.id,
      'scenario',
      { enabled }
    );
    useAlert(t('JUYANG_AI.SCENARIOS.STATUS_SUCCESS'));
    await loadData();
  } catch (error) {
    useAlert(errorMessage(error));
  }
};

const deleteScenario = async scenario => {
  if (!window.confirm(t('JUYANG_AI.SCENARIOS.DELETE_CONFIRM'))) return;

  try {
    await JuyangAiAPI.deleteNested(props.assistantId, 'scenarios', scenario.id);
    useAlert(t('JUYANG_AI.SCENARIOS.DELETE_SUCCESS'));
    await loadData();
    emit('changed');
  } catch (error) {
    useAlert(errorMessage(error));
  }
};

const toolNames = scenario =>
  scenario.tool_ids
    .map(id => tools.value.find(tool => tool.id === Number(id))?.name)
    .filter(Boolean)
    .join(', ');

watch(() => props.assistantId, loadData, { immediate: true });
</script>

<template>
  <section class="flex flex-col gap-5">
    <div class="flex items-start justify-between gap-4">
      <div>
        <h2 class="text-base font-medium text-n-slate-12">
          {{ t('JUYANG_AI.SCENARIOS.TITLE') }}
        </h2>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ t('JUYANG_AI.SCENARIOS.DESCRIPTION') }}
        </p>
      </div>
      <Button
        v-if="editable"
        icon="i-lucide-plus"
        :label="t('JUYANG_AI.SCENARIOS.ADD')"
        size="sm"
        @click="beginCreate"
      />
    </div>

    <form
      v-if="showForm"
      class="flex flex-col gap-4 border-y border-n-weak py-5"
      @submit.prevent="saveScenario"
    >
      <div class="grid gap-4 md:grid-cols-2">
        <Input
          v-model="form.name"
          :label="t('JUYANG_AI.SCENARIOS.NAME')"
          required
        />
        <div class="flex items-end justify-between gap-4 pb-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('JUYANG_AI.ENABLED') }}
          </span>
          <Switch v-model="form.enabled" />
        </div>
      </div>
      <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
        {{ t('JUYANG_AI.SCENARIOS.DESCRIPTION_LABEL') }}
        <textarea
          v-model="form.description"
          class="reset-base min-h-20 w-full resize-y rounded-lg border-0 bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12 outline outline-1 -outline-offset-1 outline-n-weak focus:outline-n-brand"
          required
        />
      </label>
      <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
        {{ t('JUYANG_AI.SCENARIOS.INSTRUCTIONS') }}
        <textarea
          v-model="form.instructions"
          class="reset-base min-h-36 w-full resize-y rounded-lg border-0 bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12 outline outline-1 -outline-offset-1 outline-n-weak focus:outline-n-brand"
          required
        />
      </label>

      <fieldset class="flex flex-col gap-2">
        <legend class="mb-1 text-sm font-medium text-n-slate-12">
          {{ t('JUYANG_AI.SCENARIOS.TOOLS') }}
        </legend>
        <p v-if="!executableTools.length" class="m-0 text-sm text-n-slate-10">
          {{ t('JUYANG_AI.SCENARIOS.NO_APPROVED_TOOLS') }}
        </p>
        <label
          v-for="tool in executableTools"
          :key="tool.id"
          class="flex items-center gap-2 text-sm text-n-slate-11"
        >
          <Checkbox
            :model-value="form.tool_ids.includes(tool.id)"
            @change="
              event => toggleToolSelection(tool.id, event.target.checked)
            "
          />
          {{ tool.name }}
        </label>
      </fieldset>

      <div class="flex justify-end gap-2">
        <Button
          type="button"
          color="slate"
          variant="ghost"
          :label="t('JUYANG_AI.CANCEL')"
          @click="resetForm"
        />
        <Button
          type="submit"
          icon="i-lucide-save"
          :label="isSaving ? t('JUYANG_AI.SAVING') : t('JUYANG_AI.SAVE')"
          :is-loading="isSaving"
        />
      </div>
    </form>

    <div
      v-if="isLoading"
      class="border-y border-n-weak py-12 text-center text-sm text-n-slate-11"
    >
      {{ t('JUYANG_AI.LOADING') }}
    </div>
    <div
      v-else-if="!scenarios.length"
      class="border-y border-n-weak py-12 text-center text-sm text-n-slate-11"
    >
      {{ t('JUYANG_AI.SCENARIOS.EMPTY') }}
    </div>
    <ul v-else class="m-0 flex list-none flex-col divide-y divide-n-weak p-0">
      <li
        v-for="scenario in scenarios"
        :key="scenario.id"
        class="flex flex-col gap-3 py-4 lg:flex-row lg:items-start lg:justify-between"
      >
        <div class="min-w-0">
          <h3 class="m-0 text-sm font-medium text-n-slate-12">
            {{ scenario.name }}
          </h3>
          <p class="m-0 mt-1 text-sm text-n-slate-11">
            {{ scenario.description }}
          </p>
          <p class="m-0 mt-2 whitespace-pre-wrap text-xs text-n-slate-10">
            {{ scenario.instructions }}
          </p>
          <p
            v-if="toolNames(scenario)"
            class="m-0 mt-2 text-xs text-n-slate-10"
          >
            {{ t('JUYANG_AI.SCENARIOS.TOOLS') }}:
            {{ toolNames(scenario) }}
          </p>
        </div>
        <div class="flex shrink-0 items-center gap-2">
          <span class="text-xs text-n-slate-11">
            {{
              scenario.enabled
                ? t('JUYANG_AI.ENABLED')
                : t('JUYANG_AI.DISABLED')
            }}
          </span>
          <Switch
            :model-value="scenario.enabled"
            :disabled="!editable"
            @change="enabled => toggleScenario(scenario, enabled)"
          />
          <Button
            v-if="editable"
            icon="i-lucide-pencil"
            color="slate"
            variant="ghost"
            size="sm"
            :title="t('JUYANG_AI.EDIT')"
            @click="beginEdit(scenario)"
          />
          <Button
            v-if="editable"
            icon="i-lucide-trash-2"
            color="ruby"
            variant="ghost"
            size="sm"
            :title="t('JUYANG_AI.DELETE')"
            @click="deleteScenario(scenario)"
          />
        </div>
      </li>
    </ul>
  </section>
</template>
